import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useEffect, useRef, useState } from "react";

import { useHighchartsTheme } from "./useHighchartsTheme";

require("highcharts/modules/exporting")(Highcharts);
require("highcharts/modules/export-data")(Highcharts);

interface Props {
  series: Highcharts.SeriesOptionsType[];
  chartTitle?: string;
  chartSubtitle?: string;
  xAxisSettings?: Highcharts.XAxisOptions | Highcharts.XAxisOptions[];
  yAxisTitle?: string;
  xAxisTitle?: string;
  enableLegend?: boolean;
  sharedTooltip?: boolean;
  reflow?: number;
}

export default function HistogramColumnChart(props: Props) {
  const theme = useHighchartsTheme();
  const [chartOptions, setChartOptions] = useState<Highcharts.Options>({});

  Highcharts.setOptions(theme);

  const ref = useRef<HighchartsReact.RefObject>(null);

  useEffect(() => {
    ref.current?.chart.reflow();
  }, [props.reflow]);

  useEffect(() => {
    let exportMenuItems = ["downloadPNG", "downloadCSV"];

    const options: Highcharts.Options = {
      chart: {
        type: "column",
      },
      time: {
        useUTC: false,
      },
      title: {
        text: props.chartTitle,
      },
      subtitle: {
        text: props.chartSubtitle,
      },
      legend: {
        enabled: props.enableLegend,
      },
      tooltip: {
        formatter: function () {
          return `<b>${this.point.name}</b><br />${props.yAxisTitle}: <b>${this.y}</b>`;
        },
      },
      xAxis: {
        type: "linear",
        title: {
          text: props.xAxisTitle,
        },
        ...props.xAxisSettings,
      },
      yAxis: {
        tickInterval: 1,
        type: "logarithmic",
        title: {
          text: props.yAxisTitle,
        },
        opposite: false,
      },
      plotOptions: {
        column: {
          pointPadding: 0,
          borderWidth: 1,
          groupPadding: 0,
          shadow: false,
        },
      },
      credits: {
        enabled: false,
      },
      exporting: {
        enabled: true,
        buttons: {
          contextButton: {
            menuItems: exportMenuItems,
          },
        },
      },
      series: props.series,
    };

    setChartOptions(options);
  }, [
    props.chartTitle,
    props.chartSubtitle,
    props.enableLegend,
    props.xAxisSettings,
    props.xAxisTitle,
    props.yAxisTitle,
    props.series,
  ]);

  return (
    <HighchartsReact
      ref={ref}
      highcharts={Highcharts}
      options={chartOptions}
      containerProps={{ style: { height: "100%", width: "100%" } }}
    />
  );
}
